import type { OpsCase } from '../routes/Cases';

/** Who a case is waiting on. Operations' own acts are told apart from everyone else's. */
export type Team = 'Operations' | 'HR' | 'Venue' | 'Lab' | 'Physician' | 'Employee' | 'None';

export interface NextStep {
  team: Team;
  /** The act, in the words of whoever does it. */
  action: string;
  /** What Operations can do about it on the case page, when it is Operations' act. */
  opsAct?: 'route' | 'book' | 'physician' | 'chase';
}

/**
 * The one next step on a case, and whose it is.
 *
 * Read from the problems the server flags first (they are why the case is
 * stuck), then from the stage. Routing, booking or rebooking at the routed
 * lab, and giving a review to a physician who can sign, are Operations' acts;
 * raising a retest is HR's; the venue, the lab and the physician own their
 * own stages. Nothing here is decided by the browser — it only names who acts
 * on what the server already says.
 */
export function nextStep(c: Pick<OpsCase, 'status' | 'exceptions' | 'physicianWait' | 'appointment'>): NextStep {
  const ex = new Set(c.exceptions);
  if (ex.has('UNASSIGNED')) return { team: 'Operations', action: 'Route to a lab', opsAct: 'route' };
  if (ex.has('AWAITING_PHYSICIAN')) {
    return c.physicianWait === 'OVERDUE'
      ? { team: 'Operations', action: 'Chase or reassign physician', opsAct: 'physician' }
      : { team: 'Operations', action: 'Assign a physician', opsAct: 'physician' };
  }
  if (ex.has('NO_SHOW')) return { team: 'Operations', action: 'Rebook', opsAct: 'book' };
  if (ex.has('NO_APPOINTMENT')) return { team: 'Operations', action: 'Book a slot', opsAct: 'book' };
  if (ex.has('SLOT_PASSED')) return { team: 'Venue', action: 'Check in or mark missed' };
  if (ex.has('RETEST_REQUIRED')) return { team: 'HR', action: 'Raise the retest' };
  switch (c.status) {
    case 'DRAFT': return { team: 'HR', action: 'Raise the case' };
    case 'REQUESTED': return { team: 'Operations', action: 'Route to a lab', opsAct: 'route' };
    case 'ROUTED': return { team: 'Operations', action: 'Book a slot', opsAct: 'book' };
    case 'CONSENT_PENDING': return { team: 'Employee', action: 'Give consent' };
    case 'SCHEDULED': return { team: 'Employee', action: 'Attend the appointment' };
    case 'NO_SHOW_OPEN': return { team: 'Operations', action: 'Rebook', opsAct: 'book' };
    case 'CHECKED_IN': case 'IN_PROGRESS': return { team: 'Venue', action: 'Finish the examination' };
    case 'LAB_PENDING': return { team: 'Lab', action: 'Release the results' };
    case 'REVIEW_PENDING': return { team: 'Physician', action: 'Review and sign' };
    case 'RETEST_REQUIRED': return { team: 'HR', action: 'Raise the retest' };
    default: return { team: 'None', action: 'Nothing left to do' };
  }
}

export const TEAM_TONE: Record<Team, 'info' | 'neutral' | 'dueSoon' | 'pending' | 'covered'> = {
  Operations: 'dueSoon', HR: 'info', Venue: 'pending', Lab: 'pending', Physician: 'pending', Employee: 'neutral', None: 'covered',
};
